// Shared helpers for the Worker Line Admin screens (Prompt 132): Super
// Agents / PLC / Agents list+create+edit+detail pages and the Worker Line
// Org Chart. Mirrors the small per-page helper functions
// ../geofence/GeofenceZonesPage.jsx defines inline, factored out here
// because four separate pages (SuperAgentsOverviewPage, PlcsListPage,
// AgentsListPage, WorkerLineOrgChartPage) all need the exact same status
// vocabulary/error copy/date formatting - WorkerLineStatuses.Active/Inactive
// is the backend's own full status set (WorkerLineAdminRecords.cs), no
// other status value is ever valid here.

export const WORKER_LINE_STATUS_OPTIONS = ["Active", "Inactive"];

const STATUS_BADGE_VARIANT = { Active: "success", Inactive: "neutral" };

export function statusBadgeVariant(status) {
  return STATUS_BADGE_VARIANT[status] ?? "neutral";
}

export function formatDateTimeTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${datePart} · ${timePart} ICT`;
}

export function describeListError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เข้าถึงรายการนี้";
  }
  return "กรุณาลองรีเฟรชหน้านี้อีกครั้ง หากยังพบปัญหากรุณาติดต่อฝ่ายปฏิบัติการ";
}

export function describeDetailError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ดูรายละเอียดนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบรายการนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  return "ไม่สามารถโหลดรายละเอียดได้ กรุณาลองใหม่อีกครั้ง";
}

export function describeWriteError(error, conflictFallback) {
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์ทำรายการนี้ เฉพาะ SuperAdmin เท่านั้นที่สร้าง/แก้ไขได้";
  }
  if (error?.status === 404) {
    return "ไม่พบรายการนี้ อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 409) {
    return conflictFallback;
  }
  if (error?.status === 400) {
    return "กรุณาตรวจสอบข้อมูลที่กรอกอีกครั้ง";
  }
  return "ไม่สามารถบันทึกรายการนี้ได้ กรุณาลองใหม่อีกครั้ง";
}

// nextActions codes come from WorkerLineAdminService's own
// BuildSuperAgentNextActions/BuildPlcNextActions/BuildAgentNextActions -
// a fixed, small vocabulary of literal action codes, never a free-text
// string. CreatePlc/CreateAgent are real navigations into this packet's own
// Create drawers (prefilled with the parent id via a query param); the
// invite/application-review actions point at a different, not-yet-built
// packet, so they render as a plain informational label instead of a link
// to a page that does not exist.
export const NEXT_ACTION_LABELS = {
  CreatePlc: "สร้าง PLC / บมจ ใต้สายงานนี้",
  CreateAgent: "สร้าง Agent ใต้สายงานนี้",
  InviteWorkers: "เชิญแรงงานเพิ่ม (Invite Card)",
  CreateInviteCard: "สร้าง Invite Card",
  ReviewApplications: "ตรวจสอบใบสมัคร",
};

export function nextActionLabel(code) {
  return NEXT_ACTION_LABELS[code] ?? code;
}

// CreatePlc/CreateAgent are the only two next-action codes this packet can
// actually navigate to (the Invite Card / Application review screens are a
// different, not-yet-built packet) - everything else renders as an inert
// informational label rather than a dead link.
export function isNavigableNextAction(code) {
  return code === "CreatePlc" || code === "CreateAgent";
}

/**
 * Human labels for every Worker Line field an operator ever sees.
 *
 * These screens used to print the raw DTO property names (`ownerName`,
 * `primaryPhoneNumber`, `createdAtUtc`, `legalName`, `lineId`) straight into
 * table headers, drawer rows and form labels. That reads as an unfinished
 * API console rather than an operations tool, and it fails the product's own
 * bilingual rule: ops staff read Thai, not camelCase. One map, used by the
 * list, the detail drawer and both forms, so the wording can never drift
 * between the three places the same field appears.
 */
export const WORKER_LINE_FIELD_LABELS = {
  displayName: "ชื่อที่ใช้แสดง",
  legalName: "ชื่อนิติบุคคล",
  ownerName: "เจ้าของ / ผู้รับผิดชอบ",
  primaryPhoneNumber: "เบอร์ติดต่อหลัก",
  lineId: "LINE ID",
  status: "สถานะ",
  note: "หมายเหตุ",
  // P161: the Agent's own picture. Lives in this same map so the create form,
  // the edit form and the detail drawer can never drift into three different
  // words for one thing.
  photo: "รูป Agent",
  createdAtUtc: "สร้างเมื่อ",
  updatedAtUtc: "แก้ไขล่าสุด",
};

export function fieldLabel(field) {
  return WORKER_LINE_FIELD_LABELS[field] ?? field;
}

// ---------------------------------------------------------------------------
// P156: a Super Agent line is one of two SIDES, and the whole hierarchy below
// it inherits that side:
//
//   ฝั่งแรงงาน  Super Agent -> บมจ (บริษัทจัดหาแรงงาน) -> Agent -> แรงงาน
//   ฝั่งงาน     Super Agent -> บจ  (บริษัทจัดหางาน)    -> Agent -> นายจ้าง
//
// P161 fixed the two middle-tier names at the owner's instruction. The side a
// company is on says whose problem it solves: a job-side company finds JOBS
// (for the employers it serves), a worker-side company finds WORKERS. The old
// wording had "บริษัทจัดหางาน" on the worker side and "บริษัทจ้างงาน" on the
// job side, which read backwards and is why the sysadmin menu now names the
// two screens บริษัทจัดหางาน and บริษัทจัดหาแรงงาน.
//
// The middle tier is the same tier and the same table on both sides; only its
// NAME and what the Agent below it holds differ. An Agent on the job side has
// no workers and sends no worker Invite Card - its line is employers.
//
// Every label the operator reads comes from here, so no screen can invent its
// own name for a side and disagree with the next one.
// ---------------------------------------------------------------------------

export const ORGANIZATION_SIDES = { WORKER_SUPPLY: "WorkerSupply", JOB_DEMAND: "JobDemand" };

// Mirrors OrganizationSides.Normalize in the backend exactly: anything that is
// not the literal "JobDemand" is the worker side. Never guess towards the job
// side - that would relax what an Agent is allowed to hold on the strength of
// a missing or misspelt value.
export function normalizeOrganizationSide(side) {
  return side === ORGANIZATION_SIDES.JOB_DEMAND ? ORGANIZATION_SIDES.JOB_DEMAND : ORGANIZATION_SIDES.WORKER_SUPPLY;
}

export function isJobDemandSide(side) {
  return normalizeOrganizationSide(side) === ORGANIZATION_SIDES.JOB_DEMAND;
}

export function organizationSideLabel(side) {
  return isJobDemandSide(side) ? "ฝั่งงาน" : "ฝั่งแรงงาน";
}

/** The middle tier's real-world name on this side: บจ on the job side, บมจ on the worker side. */
export function middleTierLabel(side) {
  return isJobDemandSide(side) ? "บจ" : "บมจ";
}

export function middleTierFullLabel(side) {
  return isJobDemandSide(side) ? "บจ (บริษัทจัดหางาน)" : "บมจ (บริษัทจัดหาแรงงาน)";
}

/**
 * P161: the middle tier's own screen name in the sysadmin menu - the same word
 * an operator would use out loud for the company, with no tier abbreviation in
 * front of it. Kept here rather than typed into navConfig.js so the menu, the
 * page heading and the empty state can never disagree about what the screen is
 * called.
 */
export function middleTierScreenLabel(side) {
  return isJobDemandSide(side) ? "บริษัทจัดหางาน" : "บริษัทจัดหาแรงงาน";
}

/** One line saying what this side's companies actually do, for the screen's own subheading. */
export function middleTierScreenDescription(side) {
  return isJobDemandSide(side)
    ? "บริษัทฝั่งงาน — สายงานใต้บริษัทเหล่านี้คือ Agent และนายจ้าง ไม่ถือแรงงานเอง"
    : "บริษัทฝั่งแรงงาน — สายงานใต้บริษัทเหล่านี้คือ Agent และแรงงานในสังกัด";
}

/** What an Agent on this side actually recruits - the word that replaces "แรงงาน". */
export function agentLineNoun(side) {
  return isJobDemandSide(side) ? "นายจ้าง" : "แรงงาน";
}

export function organizationSideBadgeVariant(side) {
  return isJobDemandSide(side) ? "info" : "neutral";
}
